<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-fault-tracking">
    <div class="kb-stat-row">
      <div v-for="item in stats" :key="item.key" class="kb-stat-card">
        <div class="kb-stat-label">{{ item.label }}</div>
        <div class="kb-stat-value" :class="item.className">{{ item.value }}</div>
        <div class="kb-kpi-sub">{{ item.sub }}</div>
      </div>
    </div>

    <el-form inline class="filter-bar" :model="filters" size="small">
      <el-form-item label="处置阶段">
        <el-select v-model="filters.stage" clearable placeholder="全部" style="width: 132px">
          <el-option v-for="item in stageOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </el-form-item>
      <el-form-item label="报警级别">
        <el-input v-model="filters.priority" placeholder="一级/二级/三级" clearable style="width: 132px" />
      </el-form-item>
      <el-form-item label="工单/设备">
        <el-input v-model="filters.keyword" placeholder="工单号或设备" clearable style="width: 180px" />
      </el-form-item>
      <el-form-item label="处理人员">
        <el-input v-model="filters.handler" placeholder="姓名" clearable style="width: 120px" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" @click="loadData">查询</el-button>
        <el-button @click="resetFilter">重置</el-button>
      </el-form-item>
    </el-form>

    <el-table
      :data="workorders"
      border
      stripe
      size="small"
      style="width: 100%"
      :row-class-name="rowClassName"
    >
      <el-table-column prop="workOrderNo" label="关联工单号" width="150">
        <template slot-scope="{ row }">
          <span>{{ row.workOrderNo || '--' }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="deviceName" label="设备名称" min-width="140" />
      <el-table-column prop="stageName" label="处置阶段" width="110">
        <template slot-scope="{ row }">
          <el-tag :type="stageTagType(row.stage)" size="mini">{{ row.stageName }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="流程节点" min-width="260">
        <template slot-scope="{ row }">
          <div class="kb-steps">
            <div
              v-for="step in row.steps"
              :key="step.key"
              class="kb-step"
              :class="stepClass(step)"
              :title="stepTitle(step)"
            >
              <span class="kb-step-dot" />
              <span class="kb-step-label">{{ step.label }}</span>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="handler" label="处理人员" width="110">
        <template slot-scope="{ row }">{{ row.handler || '--' }}</template>
      </el-table-column>
      <el-table-column prop="receiveTime" label="接单时间" width="160">
        <template slot-scope="{ row }">{{ formatTime(row.receiveTime) }}</template>
      </el-table-column>
      <el-table-column label="当前进度" width="150">
        <template slot-scope="{ row }">
          <el-progress
            :percentage="safeProgress(row.progress)"
            :stroke-width="10"
            :status="row.overdue ? 'exception' : undefined"
          />
        </template>
      </el-table-column>
      <el-table-column prop="expectedFinishTime" label="预计完成时间" width="180">
        <template slot-scope="{ row }">
          <span>{{ formatTime(row.expectedFinishTime) }}</span>
          <el-tag v-if="row.overdue" type="danger" size="mini" class="kb-overdue-tag">超时</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="88" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" @click="openDetail(row)">详情</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog title="处置详情" :visible.sync="detailVisible" width="560px" append-to-body>
      <template v-if="current">
        <div class="kb-detail-line"><strong>关联工单：</strong>{{ current.workOrderNo || '--' }}</div>
        <div class="kb-detail-line"><strong>报警内容：</strong>{{ current.description || '--' }}</div>
        <div class="kb-detail-line"><strong>预计完成：</strong>{{ formatTime(current.expectedFinishTime) }}</div>
        <el-divider content-position="left">流程记录</el-divider>
        <ul class="kb-history">
          <li v-for="(item, index) in current.history" :key="index">{{ item }}</li>
        </ul>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchFaultTracking } from './api'

export default {
  name: 'KanbanFaultTracking',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      workorders: [],
      summary: {
        total: 0,
        inProgress: 0,
        overdue: 0,
        avgProgress: 0
      },
      timeoutMinutes: 60,
      filters: {
        stage: '',
        priority: '',
        keyword: '',
        handler: ''
      },
      stageOptions: [
        { value: 0, label: '待检测' },
        { value: 1, label: '已派单' },
        { value: 2, label: '维修中' },
        { value: 3, label: '待验收' },
        { value: 4, label: '已完成' }
      ],
      detailVisible: false,
      current: null
    }
  },
  computed: {
    stats() {
      return [
        { key: 'total', label: '总工单数', value: this.summary.total || 0, sub: '当前筛选' },
        { key: 'inProgress', label: '处理中', value: this.summary.inProgress || 0, sub: '未闭环工单' },
        {
          key: 'overdue',
          label: '超时预警',
          value: this.summary.overdue || 0,
          sub: `${this.timeoutMinutes}分钟阈值`,
          className: this.summary.overdue ? 'is-danger' : ''
        },
        { key: 'avgProgress', label: '平均进度', value: `${this.summary.avgProgress || 0}%`, sub: '整体完成度' }
      ]
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const data = await fetchFaultTracking(this.buildParams())
        this.workorders = data.workorders || []
        this.summary = data.stats || this.summary
        this.timeoutMinutes = data.timeoutMinutes || this.timeoutMinutes
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    buildParams() {
      return {
        stage: this.filters.stage === '' ? null : this.filters.stage,
        priority: this.filters.priority || null,
        keyword: this.filters.keyword || null,
        handler: this.filters.handler || null
      }
    },
    resetFilter() {
      this.filters = {
        stage: '',
        priority: '',
        keyword: '',
        handler: ''
      }
      this.loadData()
    },
    rowClassName({ row }) {
      return row && row.overdue ? 'is-overdue-row' : ''
    },
    stepClass(step) {
      return `is-${step.status || 'pending'}`
    },
    stepTitle(step) {
      const time = step.time ? ` ${step.time}` : ''
      const handler = step.handler ? ` ${step.handler}` : ''
      return `${step.label}${handler}${time}`
    },
    stageTagType(stage) {
      if (stage === 4) return 'success'
      if (stage === 3) return 'warning'
      if (stage === 2) return ''
      if (stage === 1) return 'info'
      return 'danger'
    },
    safeProgress(value) {
      const number = Number(value || 0)
      if (number < 0) return 0
      if (number > 100) return 100
      return number
    },
    formatTime(value) {
      return value || '--'
    },
    openDetail(row) {
      this.current = row
      this.detailVisible = true
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
